/**
 * crmFormHelpers.js - the small pure rules the CRM forms share (Phase 1.2):
 * the first-task check (T-03 / Paul Q11), the account / site picker grouping
 * (C-02), the contact save payload and export row (C-02 / C-03 / C-04 / C-05)
 * and the contacts-list order (primary first, Left company last).
 *
 * Kept free of React so crmFormHelpers.test.js can check them directly.
 */
import { pickField, isLeftCompany, LEFT_COMPANY } from '../../data/crmStages';
import { tidyTagged } from '../tasks/tagged';

const clean = (v) => (v == null || v === '-' ? '' : String(v));
const orNull = (v) => (v != null && String(v).trim() ? String(v).trim() : null);

/**
 * T-03: a new site or site-less account needs a complete first task.
 * Returns { description?, owner?, due_date? } - one plain-English message per
 * missing piece; an empty object means the task is complete.
 */
export function firstTaskErrors(task) {
  const t = task || {};
  const errs = {};
  if (!String(t.description || '').trim()) errs.description = 'Describe the first task - what happens next.';
  if (!String(t.owner || '').trim()) errs.owner = 'Pick who owns the first task.';
  if (!String(t.due_date || '').trim()) errs.due_date = 'Pick a due date for the first task.';
  return errs;
}

/**
 * The body the API expects for a first task / next step. `tagged` (the people
 * tagged besides the owner) goes only when someone is tagged, never the owner.
 */
export const taskPayload = (t) => {
  const tagged = tidyTagged(t.tagged, t.owner);
  return {
    description: String(t.description || '').trim(),
    owner: t.owner,
    due_date: t.due_date,
    ...(tagged.length ? { tagged } : {}),
  };
};

/**
 * C-02: accounts for a picker - each company, followed by its sites. A site
 * whose parent is missing is listed as a company so it is never lost.
 * Returns [{ company, sites: [...] }] sorted by name.
 */
export function groupAccountsForPicker(accounts) {
  const list = accounts || [];
  const ids = new Set(list.map((a) => a.id));
  const parentOf = (a) => pickField(a, 'parentAccountId', 'parent_account_id');
  const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''));
  const companies = list.filter((a) => !parentOf(a) || !ids.has(parentOf(a))).sort(byName);
  return companies.map((company) => ({
    company,
    sites: list.filter((s) => parentOf(s) === company.id).sort(byName),
  }));
}

/** An https link for a LinkedIn URL typed without its scheme ("linkedin.com/in/x"). */
export function linkedinHref(url) {
  const s = String(url || '').trim();
  if (!s) return '';
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
}

/**
 * Contact form -> API body. The old free-text "role" column is shown as
 * Title; Role is the C-03 picklist (contact_role). Blank stays blank (null).
 * C-04: a contact who left the company is never the primary - the API
 * refuses is_primary with Left company, so the box is sent unticked.
 */
export function contactPayload(f) {
  const left = f.contactStatus === LEFT_COMPANY;
  return {
    name: String(f.name || '').trim(),
    role: orNull(f.role),
    contact_role: orNull(f.contactRole),
    email: orNull(f.email),
    phone: orNull(f.phone),
    linkedin_url: orNull(f.linkedinUrl),
    is_primary: left ? false : !!f.isPrimary,
    contact_status: left ? LEFT_COMPANY : 'Active',
  };
}

/** A stored contact -> the edit form's fields (either casing). */
export function contactFormFrom(c) {
  return {
    name: c.name || '',
    role: clean(c.role),
    contactRole: pickField(c, 'contactRole', 'contact_role') || '',
    email: clean(c.email),
    phone: clean(c.phone),
    linkedinUrl: pickField(c, 'linkedinUrl', 'linkedin_url') || '',
    isPrimary: !!pickField(c, 'isPrimary', 'is_primary'),
    contactStatus: isLeftCompany(c) ? LEFT_COMPANY : 'Active',
    accountId: pickField(c, 'accountId', 'account_id') || '',
  };
}

/** One contacts-export row (C-02 / C-03 / C-04 / C-05). `acct` is the contact's own account or site. */
export function contactExportRow(c, acct) {
  const parent = acct ? pickField(acct, 'parentName', 'parent_name') : null;
  return {
    Name: clean(c.name),
    Title: clean(c.role),
    Role: clean(pickField(c, 'contactRole', 'contact_role')),
    Email: clean(c.email),
    Phone: clean(c.phone),
    LinkedIn: clean(pickField(c, 'linkedinUrl', 'linkedin_url')),
    Primary: pickField(c, 'isPrimary', 'is_primary') ? 'Yes' : '',
    Status: isLeftCompany(c) ? LEFT_COMPANY : 'Active',
    'Account / Site': clean(c.accountName || (acct && acct.name)),
    'Parent Account': clean(parent),
  };
}

/**
 * A contacts list in reading order: primary first, anyone who left the
 * company last (C-04 - they stay on file, below the people you can call).
 * Stable, so the rest keep the order they came in.
 */
export function sortContactsForList(list) {
  const primary = (c) => (pickField(c, 'isPrimary', 'is_primary') ? 0 : 1);
  const left = (c) => (isLeftCompany(c) ? 1 : 0);
  return [...(list || [])].sort((a, b) => left(a) - left(b) || primary(a) - primary(b));
}
